import type {
  QueryResult,
  SchemaTable,
  SaveResult,
  SessionInfo,
  DataCheckResult,
  PushCheckResult,
  GithubStatus,
  GithubRepo,
  DeviceFlowStart,
  DeviceFlowStatus,
  PageResponse,
  CommitState,
  RepoStatus,
  TreeResponse,
  SearchResponse,
  AccessOverview,
  OrchestratorStatus,
  ShareInfo,
} from "../shared/types.js";
import { SESSION_ID } from "./data/session.js";

/** Base path for a repo-scoped API call: `/api/r/<repo>`. */
function R(repo: string): string {
  return `/api/r/${encodeURIComponent(repo)}`;
}

/** URL that streams a repo file's raw bytes (draft-first, so a just-uploaded
 * asset shows before publish). Same route the markdown renderer rewrites images
 * to — exported for panels that preview an uploaded file directly. */
export function blobUrl(repo: string, path: string): string {
  return `${R(repo)}/blob/${path.split("/").map(encodeURIComponent).join("/")}`;
}

async function jsonOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) msg = body.error;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  return res.json() as Promise<T>;
}

export async function fetchSession(): Promise<SessionInfo> {
  return jsonOrThrow<SessionInfo>(await fetch("/api/me"));
}

export async function fetchAccess(): Promise<AccessOverview> {
  return jsonOrThrow<AccessOverview>(await fetch("/api/access"));
}

export async function fetchStatus(): Promise<OrchestratorStatus> {
  return jsonOrThrow<OrchestratorStatus>(await fetch("/api/status"));
}

/** Switch the worn hat (null = no hat). */
export const setHat = (current: string | null): Promise<void> => putHats({ current });

async function putHats(body: unknown): Promise<void> {
  await jsonOrThrow(
    await fetch("/api/hats", {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    }),
  );
}

/** Create a new hat and wear it. */
export const addHat = (name: string): Promise<void> => putHats({ add: name });
/** Drop a hat (clears it if currently worn). */
export const removeHat = (name: string): Promise<void> => putHats({ remove: name });
/** Rename a hat (follows the worn hat). */
export const renameHat = (from: string, to: string): Promise<void> =>
  putHats({ rename: { from, to } });

export async function fetchTree(repo: string): Promise<TreeResponse> {
  return jsonOrThrow<TreeResponse>(await fetch(`${R(repo)}/tree`));
}

/** Search one repo: the dwsearch `fts` index — BM25-ranked, main-only, embeds
 *  excluded, prefix/typo-expanded. There is exactly one backend; the `git grep`
 *  one was dropped (docs/wiki/Indexing.md "git grep — dropped"). */
export async function searchRepo(repo: string, q: string): Promise<SearchResponse> {
  return jsonOrThrow<SearchResponse>(
    // x-dw-session scopes the search's span to this browser's live tasks card.
    await fetch(`${R(repo)}/search?q=${encodeURIComponent(q)}`, {
      headers: { "x-dw-session": SESSION_ID },
    }),
  );
}

/** One semantic hit: a page near the submitted text. `score` is cosine similarity
 *  (1 = identical) over normalized vectors. See docs/wiki/Vss.md. */
export interface RelatedHit {
  repo: string;
  path: string;
  score: number;
  text: string;
  line: number;
}

/** Find pages semantically nearest to `text`. A POST because the input is a
 *  document (a page, a transcript), not a query string. */
export async function fetchRelated(
  text: string,
  limit = 10,
  excludePath?: string,
): Promise<{ hits: RelatedHit[]; model: string }> {
  return jsonOrThrow<{ hits: RelatedHit[]; model: string }>(
    await fetch(`/api/related`, {
      method: "POST",
      headers: { "content-type": "application/json", "x-dw-session": SESSION_ID },
      body: JSON.stringify({ text, limit, excludePath }),
    }),
  );
}

/** Embedding coverage — partial is the normal state, so surfaces can say so. */
export async function fetchRelatedStatus(): Promise<{
  total: number;
  done: number;
  ready: boolean;
  model: string;
}> {
  return jsonOrThrow(await fetch(`/api/related/status`));
}

export async function fetchPage(repo: string, path: string): Promise<PageResponse> {
  return jsonOrThrow<PageResponse>(await fetch(`${R(repo)}/page/${path}`));
}

export async function fetchRaw(
  repo: string,
  path: string,
): Promise<{ path: string; content: string }> {
  return jsonOrThrow(await fetch(`${R(repo)}/raw/${path}`));
}

// Every draft mutation carries `x-dw-session`. The server pushes a `change`
// frame to the user's *other* tabs so they re-render (livebus `broadcastDraft`);
// naming the session that caused the write is what keeps the frame from bouncing
// back here and refetching the page this tab just saved.
const MUTATE = { "x-dw-session": SESSION_ID };

export async function saveContent(
  repo: string,
  path: string,
  content: string,
): Promise<SaveResult> {
  return jsonOrThrow<SaveResult>(
    await fetch(`${R(repo)}/save/${path}`, {
      method: "POST",
      headers: { ...MUTATE, "content-type": "text/plain" },
      body: content,
    }),
  );
}

export async function createFile(repo: string, path: string): Promise<SaveResult> {
  return jsonOrThrow<SaveResult>(
    await fetch(`${R(repo)}/file/${path}`, { method: "POST", headers: MUTATE }),
  );
}

export async function deleteFile(repo: string, path: string): Promise<SaveResult> {
  return jsonOrThrow<SaveResult>(
    await fetch(`${R(repo)}/file/${path}`, { method: "DELETE", headers: MUTATE }),
  );
}

export async function copyFile(
  repo: string,
  from: string,
  to: string,
): Promise<{ ok: true; from: string; to: string; branch: string }> {
  return jsonOrThrow(
    await fetch(`${R(repo)}/copy`, {
      method: "POST",
      headers: { ...MUTATE, "content-type": "application/json" },
      body: JSON.stringify({ from, to }),
    }),
  );
}

export async function moveFile(
  repo: string,
  from: string,
  to: string,
): Promise<{ ok: true; from: string; to: string; branch: string }> {
  return jsonOrThrow(
    await fetch(`${R(repo)}/move`, {
      method: "POST",
      headers: { ...MUTATE, "content-type": "application/json" },
      body: JSON.stringify({ from, to }),
    }),
  );
}

export async function runQuery(
  repo: string,
  sql: string,
  opts: {
    /** Explicit freshness window — overrides the query's `@maxAge` pragma. */
    ttl?: string;
    /** Fallback window when neither `ttl` nor a pragma is present
     * (embeds send "forever" — the permanent-cache default). */
    maxAgeDefault?: string;
    force?: boolean;
    values?: Record<string, string>;
    path?: string;
    /** DOM id of the embed that ran this, so its live task can scroll back. */
    embedId?: string;
  } = {},
): Promise<QueryResult> {
  const headers: Record<string, string> = {
    "content-type": "application/json",
    "x-dw-session": SESSION_ID,
  };
  if (opts.embedId) headers["x-dw-embed"] = opts.embedId;
  return jsonOrThrow<QueryResult>(
    await fetch(`${R(repo)}/query`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        sql,
        ttl: opts.ttl,
        maxAgeDefault: opts.maxAgeDefault,
        force: opts.force,
        values: opts.values,
        path: opts.path,
      }),
    }),
  );
}

export async function fetchSchema(): Promise<SchemaTable[]> {
  return jsonOrThrow<SchemaTable[]>(await fetch("/api/schema"));
}

export interface LocalSetup {
  repoName: string;
  repoUrl: string;
  ourUrl: string;
  tgzUrl: string;
  configuredRemote: string | null;
  mode: "none" | "match" | "mismatch";
  cmd: string;
  /** No-install alternative: `npx --registry=…/_npm/ @datawiki/datawiki …`. */
  npxCmd: string;
  steps: string[];
}

/** Data for the "run locally" page (computed server-side from config + repo).
 * `repo` selects which content repo to set up (the /_local dropdown). */
export async function fetchLocalSetup(repo: string): Promise<LocalSetup> {
  return jsonOrThrow<LocalSetup>(
    await fetch(`/api/local?repo=${encodeURIComponent(repo)}`),
  );
}

export interface SourceInfo {
  id: string;
  label: string;
  dialect: string;
  /** Where a query with no `-- @source:` runs. Exactly one source has it. */
  isDefault: boolean;
  /** Reachable right now — DOWN means queries against it can't be saved. */
  up: boolean;
  tables: string[];
  error?: string;
}

/** Registered data sources + their tables, for the /_data source tree. */
export async function fetchSources(force = false): Promise<SourceInfo[]> {
  // force=1 bypasses the server's 5-min liveness cache (the data card's ↻).
  return jsonOrThrow<SourceInfo[]>(
    await fetch(`/api/sources${force ? "?force=1" : ""}`),
  );
}

/**
 * Upload a file into the editing draft, co-located under the page's `assets/`
 * dir. Returns `url` — the relative reference to embed (`./assets/<name>`) —
 * the repo `path` it landed at, and whether the server classed it as an image
 * (which is what decides `![x](url)` versus `[x](url)`).
 *
 * The reference stays relative on disk so the page still renders on GitHub or
 * in a plain checkout; only the *render* rewrites it to `/api/blob/…`.
 */
export async function uploadAsset(
  repo: string,
  file: File,
  pagePath: string,
): Promise<{ url: string; path: string; isImage: boolean }> {
  const q = `name=${encodeURIComponent(file.name)}&page=${encodeURIComponent(pagePath)}`;
  return jsonOrThrow(
    await fetch(`${R(repo)}/upload?${q}`, {
      method: "POST",
      headers: {
        ...MUTATE,
        "content-type": file.type || "application/octet-stream",
        accept: "application/json",
      },
      body: file,
    }),
  );
}

/** Probe one configured database's live connection. `source` is its name (its
 * `databases/<name>/` directory); omitted = the default database. */
export async function checkConnection(source?: string): Promise<DataCheckResult> {
  return jsonOrThrow<DataCheckResult>(
    await fetch("/api/data/check", {
      method: "POST",
      headers: { accept: "application/json", "content-type": "application/json" },
      body: JSON.stringify({ source }),
    }),
  );
}

export async function checkPush(): Promise<PushCheckResult> {
  return jsonOrThrow<PushCheckResult>(
    await fetch("/api/push/check", {
      method: "POST",
      headers: { accept: "application/json" },
    }),
  );
}

export async function githubStatus(): Promise<GithubStatus> {
  return jsonOrThrow<GithubStatus>(await fetch("/api/github/status"));
}

export async function githubRepos(): Promise<GithubRepo[]> {
  return jsonOrThrow<GithubRepo[]>(await fetch("/api/github/repos"));
}

export async function githubAddRepo(repo: {
  fullName: string;
  cloneUrl: string;
  branch: string;
}): Promise<{ ok: true; name: string; canPush: boolean }> {
  return jsonOrThrow(
    await fetch("/api/github/repos/add", {
      method: "POST",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify(repo),
    }),
  );
}

export async function githubConnect(): Promise<DeviceFlowStart> {
  return jsonOrThrow<DeviceFlowStart>(
    await fetch("/api/github/connect", {
      method: "POST",
      headers: { accept: "application/json" },
    }),
  );
}

export async function githubConnectStatus(
  deviceCode: string,
): Promise<DeviceFlowStatus> {
  return jsonOrThrow<DeviceFlowStatus>(
    await fetch("/api/github/connect/status", {
      method: "POST",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify({ deviceCode }),
    }),
  );
}

export async function githubDisconnect(): Promise<{ ok: true }> {
  return jsonOrThrow(
    await fetch("/api/github/disconnect", {
      method: "POST",
      headers: { accept: "application/json" },
    }),
  );
}

export async function fetchCommitState(repo: string): Promise<CommitState> {
  return jsonOrThrow<CommitState>(await fetch(`${R(repo)}/commit`));
}

export async function fetchRepoStatus(repo: string): Promise<RepoStatus> {
  return jsonOrThrow<RepoStatus>(await fetch(`${R(repo)}/repo`));
}

export async function pullRepo(repo: string): Promise<{ ok: true }> {
  return jsonOrThrow(
    await fetch(`${R(repo)}/repo/pull`, {
      method: "POST",
      headers: { accept: "application/json" },
    }),
  );
}

export async function pushRepo(repo: string): Promise<{ ok: true }> {
  return jsonOrThrow(
    await fetch(`${R(repo)}/repo/push`, {
      method: "POST",
      headers: { accept: "application/json" },
    }),
  );
}

/** Recover a diverged main: replay local commits onto origin, then push. */
export async function rebaseRepo(repo: string): Promise<{ ok: true }> {
  return jsonOrThrow(
    await fetch(`${R(repo)}/repo/rebase`, {
      method: "POST",
      headers: { accept: "application/json" },
    }),
  );
}

/** Back the diverged HEAD up to a dw-main-<sha> branch on origin. */
export async function backupRepo(
  repo: string,
): Promise<{ ok: true; ref: string }> {
  return jsonOrThrow(
    await fetch(`${R(repo)}/repo/backup`, {
      method: "POST",
      headers: { accept: "application/json" },
    }),
  );
}

/** Hard-reset main onto origin (guarded: needs an existing backup branch). */
export async function resetRepo(repo: string): Promise<{ ok: true }> {
  return jsonOrThrow(
    await fetch(`${R(repo)}/repo/reset`, {
      method: "POST",
      headers: { accept: "application/json" },
    }),
  );
}

export interface CommitResult {
  ok: true;
  merged?: boolean;
  pushed?: boolean;
  /** Set by a `merge` that landed on local main but couldn't push to the
   * upstream (GitHub not connected, or the push was rejected). */
  pushError?: string | null;
}

export async function postCommit(
  repo: string,
  message: string,
  action: "commit" | "merge" | "rebase" | "abandon",
): Promise<CommitResult> {
  return jsonOrThrow<CommitResult>(
    await fetch(`${R(repo)}/commit`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ message, action }),
    }),
  );
}

export async function fetchGadsStatus(): Promise<{ set: boolean }> {
  return jsonOrThrow(await fetch("/api/gads/status"));
}

export async function saveGadsDevToken(token: string): Promise<void> {
  await jsonOrThrow(
    await fetch("/api/gads/dev-token", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ token }),
    }),
  );
}

export async function clearGadsDevToken(): Promise<void> {
  await jsonOrThrow(await fetch("/api/gads/dev-token", { method: "DELETE" }));
}

export interface GoogleDataStatus {
  configured: boolean;
  connected: boolean;
  email: string | null;
  scopes: string[];
}

export async function fetchGoogleDataStatus(): Promise<GoogleDataStatus> {
  return jsonOrThrow<GoogleDataStatus>(await fetch("/api/google-data/status"));
}

export async function disconnectGoogleData(): Promise<void> {
  await jsonOrThrow(
    await fetch("/api/google-data/disconnect", { method: "POST" }),
  );
}

export interface CheckResult {
  ok: boolean;
  /** Which step failed: dwsql compile (refs/params) or the engine's prepare. */
  stage?: "compile" | "prepare";
  error?: string;
}

/** Validate a query without executing it (compile + the source's prepare). */
export async function checkQuery(
  repo: string,
  sql: string,
  path?: string,
): Promise<CheckResult> {
  return jsonOrThrow<CheckResult>(
    await fetch(`${R(repo)}/check`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ sql, path }),
    }),
  );
}

export type BackfillTarget = "ga" | "ads" | "all";

/** Kick off a backfill into dwstats. `ga` / `ads` run one provider (an Ads
 * failure is then a real task error, not a silent skip); `all` is the
 * combined CLI / scheduled-refresh unit. */
export async function startBackfill(
  target: BackfillTarget = "all",
): Promise<{ id: number }> {
  return jsonOrThrow<{ id: number }>(
    await fetch("/api/google-data/backfill", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ target }),
    }),
  );
}

export interface ExportKey {
  token: string;
  url: string;
}

export async function fetchExportKey(
  repo: string,
  path: string,
): Promise<ExportKey | null> {
  const rows = await jsonOrThrow<ExportKey[]>(
    await fetch(`${R(repo)}/export-keys?path=${encodeURIComponent(path)}`),
  );
  return rows[0] ?? null;
}

export async function createExportKey(
  repo: string,
  path: string,
): Promise<ExportKey> {
  return jsonOrThrow<ExportKey>(
    await fetch(`${R(repo)}/export-keys`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ path }),
    }),
  );
}

export async function revokeExportKey(repo: string, token: string): Promise<void> {
  await jsonOrThrow(
    await fetch(`${R(repo)}/export-keys/${encodeURIComponent(token)}`, {
      method: "DELETE",
    }),
  );
}

export interface OAuthClient {
  clientId: string;
  name: string;
  redirectUris: string[];
  createdAt?: string;
}

/** External apps the current user has registered to query datawiki via OAuth. */
export async function fetchOAuthClients(): Promise<OAuthClient[]> {
  return jsonOrThrow<OAuthClient[]>(await fetch("/api/oauth/clients"));
}

export async function registerOAuthClient(
  name: string,
  redirectUris: string[],
): Promise<OAuthClient> {
  return jsonOrThrow<OAuthClient>(
    await fetch("/api/oauth/clients", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ name, redirectUris }),
    }),
  );
}

export async function deleteOAuthClient(clientId: string): Promise<void> {
  await jsonOrThrow(
    await fetch(`/api/oauth/clients/${encodeURIComponent(clientId)}`, {
      method: "DELETE",
    }),
  );
}

// --- Per-user git token (clone/push datawiki-hosted repos) ---

export async function gitTokenStatus(): Promise<{ set: boolean }> {
  return jsonOrThrow(await fetch("/api/git-token"));
}

/** Create or regenerate the user's git token — returned once. */
export async function createGitToken(): Promise<{ token: string }> {
  return jsonOrThrow(await fetch("/api/git-token", { method: "POST" }));
}

export async function revokeGitToken(): Promise<void> {
  await jsonOrThrow(await fetch("/api/git-token", { method: "DELETE" }));
}

// --- Guest shares (docs/auth/Shares.md) — `path` is the page URL path; the
// server resolves it to the file (foo → foo.md, dir → index.md).

export async function fetchShares(
  repo: string,
  path: string,
  dir: boolean,
): Promise<ShareInfo[]> {
  const q = new URLSearchParams({ path, ...(dir ? { dir: "1" } : {}) });
  return jsonOrThrow(await fetch(`${R(repo)}/shares?${q.toString()}`));
}

/** `days`: lifetime, 0 = never expires. */
export async function createShare(
  repo: string,
  path: string,
  dir: boolean,
  email: string,
  days: number,
): Promise<ShareInfo> {
  return jsonOrThrow(
    await fetch(`${R(repo)}/shares`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ path, dir, email, days }),
    }),
  );
}

export async function revokeShare(repo: string, token: string): Promise<void> {
  await jsonOrThrow(
    await fetch(`${R(repo)}/shares/${encodeURIComponent(token)}`, { method: "DELETE" }),
  );
}
